import { toast } from "@/lib/toast";
import { apiClient } from "@/services/api";
import type {
  BulkUpdateTransactionsData,
  Transaction,
  TransactionFilters,
  TransactionFormData,
} from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dashboardKeys } from "@/pages/dashboard/hooks/useDashboard";
import { accountKeys } from "./useAccounts";

// Query keys
export const transactionKeys = {
  all: ["transactions"] as const,
  lists: () => [...transactionKeys.all, "list"] as const,
  list: (filters?: TransactionFilters) => [...transactionKeys.lists(), { filters }] as const,
  details: () => [...transactionKeys.all, "detail"] as const,
  detail: (id: number) => [...transactionKeys.details(), id] as const,
  duplicates: () => [...transactionKeys.all, "duplicates"] as const,
  duplicatesForPeriod: (year: number, month?: number) =>
    [...transactionKeys.duplicates(), { year, month }] as const,
};

// Hooks
export function useTransactions(filters?: TransactionFilters) {
  return useQuery({
    queryKey: transactionKeys.list(filters),
    queryFn: () => apiClient.getTransactions(filters),
  });
}

export function useTransaction(id: number) {
  return useQuery({
    queryKey: transactionKeys.detail(id),
    queryFn: () => apiClient.getTransaction(id),
    enabled: !!id,
  });
}

export function useCreateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TransactionFormData) => apiClient.createTransaction(data),
    onSuccess: () => {
      // Invalidate all transaction queries
      queryClient.invalidateQueries({ queryKey: transactionKeys.lists() });
      // Also invalidate dashboard and accounts (balance may have changed)
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
      queryClient.invalidateQueries({ queryKey: accountKeys.all });

      toast({
        title: "Success",
        description: "Transaction created successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create transaction",
        variant: "destructive",
      });
    },
  });
}

export function useUpdateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<TransactionFormData> }) =>
      apiClient.updateTransaction(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: transactionKeys.detail(id) });

      // Snapshot previous value
      const previousTransaction = queryClient.getQueryData<Transaction>(transactionKeys.detail(id));

      // Optimistically update transaction detail
      if (previousTransaction) {
        queryClient.setQueryData<Transaction>(transactionKeys.detail(id), {
          ...previousTransaction,
          ...data,
        });
      }

      // Also update in lists (find and update in all cached list queries)
      queryClient.setQueriesData<{
        transactions: Transaction[];
        total: number;
      }>({ queryKey: transactionKeys.lists() }, (old) => {
        if (!old) return old;
        return {
          ...old,
          transactions: old.transactions.map((transaction) =>
            transaction.id === id ? { ...transaction, ...data } : transaction,
          ),
        };
      });

      return { previousTransaction };
    },
    onError: (error: Error, variables, context) => {
      // Rollback on error
      if (context?.previousTransaction) {
        queryClient.setQueryData(transactionKeys.detail(variables.id), context.previousTransaction);
      }

      toast({
        title: "Error",
        description: error.message || "Failed to update transaction",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Transaction updated successfully",
      });
    },
    onSettled: (data, error, variables) => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: transactionKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: transactionKeys.detail(variables.id),
      });
      // Update dashboard and accounts
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
      queryClient.invalidateQueries({ queryKey: accountKeys.all });
    },
  });
}

export function useDeleteTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => apiClient.deleteTransaction(id),
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: transactionKeys.lists() });

      // Snapshot previous values
      const previousLists = queryClient.getQueriesData<{
        transactions: Transaction[];
        total: number;
      }>({ queryKey: transactionKeys.lists() });

      // Optimistically remove from all lists
      queryClient.setQueriesData<{
        transactions: Transaction[];
        total: number;
      }>({ queryKey: transactionKeys.lists() }, (old) => {
        if (!old) return old;
        return {
          transactions: old.transactions.filter((transaction) => transaction.id !== id),
          total: old.total - 1,
        };
      });

      return { previousLists };
    },
    onError: (error: Error, id, context) => {
      // Rollback on error
      if (context?.previousLists) {
        for (const [queryKey, data] of context.previousLists) {
          queryClient.setQueryData(queryKey, data);
        }
      }

      toast({
        title: "Error",
        description: error.message || "Failed to delete transaction",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Transaction deleted successfully",
      });
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: transactionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
      queryClient.invalidateQueries({ queryKey: accountKeys.all });
    },
  });
}

export function useFindDuplicates(year: number, month?: number) {
  return useQuery({
    queryKey: transactionKeys.duplicatesForPeriod(year, month),
    queryFn: () => apiClient.findDuplicateTransactions(year, month),
    enabled: !!year,
  });
}

export function useBulkDeleteTransactions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (transactionIds: number[]) => apiClient.bulkDeleteTransactions(transactionIds),
    onSuccess: (_, transactionIds) => {
      // Invalidate all relevant queries
      queryClient.invalidateQueries({ queryKey: transactionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: transactionKeys.duplicates() });
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
      queryClient.invalidateQueries({ queryKey: accountKeys.all });

      toast({
        title: "Success",
        description: `${transactionIds.length} transactions deleted successfully`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete transactions",
        variant: "destructive",
      });
    },
  });
}

export function useBulkUpdateTransactions() {
  const queryClient = useQueryClient();

  const applyAction = (ids: number[], data: BulkUpdateTransactionsData): string => {
    if (data.isReviewed === true) return "marked reviewed";
    if (data.isReviewed === false) return "marked unreviewed";
    if ("categoryId" in data) return data.categoryId === null ? "uncategorized" : "categorized";
    return "updated";
  };

  return useMutation({
    mutationFn: ({
      transactionIds,
      data,
    }: {
      transactionIds: number[];
      data: BulkUpdateTransactionsData;
    }) => apiClient.bulkUpdateTransactions(transactionIds, data),
    onSuccess: (result, { transactionIds, data }) => {
      queryClient.invalidateQueries({ queryKey: transactionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: transactionKeys.duplicates() });
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
      queryClient.invalidateQueries({ queryKey: accountKeys.all });

      toast({
        title: "Success",
        description: `${result.updatedCount} of ${transactionIds.length} transactions ${applyAction(transactionIds, data)}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update transactions",
        variant: "destructive",
      });
    },
  });
}

export function useMergeTransactions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (transactionIds: number[]) => apiClient.mergeTransactions(transactionIds),
    onSuccess: (mergedTransaction, transactionIds) => {
      // Invalidate all relevant queries
      queryClient.invalidateQueries({ queryKey: transactionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: transactionKeys.duplicates() });
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
      queryClient.invalidateQueries({ queryKey: accountKeys.all });

      toast({
        title: "Success",
        description: `${transactionIds.length} transactions merged successfully`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to merge transactions",
        variant: "destructive",
      });
    },
  });
}

export function useParseTransactionFilterWithAI() {
  return useMutation({
    mutationFn: (query: string) => apiClient.parseTransactionFilterWithAI(query),
  });
}
